import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, forkJoin } from 'rxjs';

import { Dataset, DatasetSubtype, DatasetType, Document } from '@app/modules/core/store/models';
import * as UserProfileSelectors from '@app/modules/core/store/user-profile/user-profile.selectors';
import * as COPSelectors from '@app/modules/cop/store/cop/cop.selectors';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { ClosestAssetCountResponse, ClosestAssetFilterDto, SelectionLayerFilter } from '@app/modules/cop/store/impacted-assets';
import { LayerTypes } from '@app/modules/cop/store/cop';
import { ImpactedAssetsApiService } from '@app/modules/cop/services/impacted-assets-api.service';

interface ImpactedAssetsSearchParams {
  dataset: Dataset;
  document: Document;
  useImperialUnits?: boolean | undefined;
  distance?: number;
  unitDisplayOptions?: {
    lowercase?: boolean;
    abbreviated?: boolean;
  };
}

interface ImpactedAssetLayerCount extends ClosestAssetCountResponse {
  datasetType: DatasetType;
  datasetSubtype: DatasetSubtype;
}

interface ImpactedAssetsSearchResults {
  distance: number;
  distanceUnit: string;
  distanceOptions: { value: number }[];
  countsByLayer: ImpactedAssetLayerCount[];
}

enum DISTANCE_UNITS {
  IMPERIAL = 'IMPERIAL',
  METRIC = 'METRIC'
}

const DISTANCE_OPTIONS_CONFIG = {
  [DISTANCE_UNITS.IMPERIAL]: {
    DEFAULT_OPTIONS: [5, 10, 25, 50],
    DEFAULT_OPTION_VALUE_KEY: 'assetsImpactedDistanceDefaultInImperialUnits',
    MAP_SETTINGS_KEY: 'assetsImpactedDistanceOptionsInImperialUnits',
    UNIT_TRANSLATION_KEYS: {
      CAMEL: 'MILES-CAMEL',
      LOWER: 'MILES-LOWER',
      ABBREVIATED: 'MILES-ABBREVIATED',
      ABBREVIATED_LOWER: 'MILES-ABBREVIATED-LOWER'
    },
    MULTIPLIER: 1609.344
  },
  [DISTANCE_UNITS.METRIC]: {
    DEFAULT_OPTIONS: [10, 15, 40, 80],
    DEFAULT_OPTION_VALUE_KEY: 'assetsImpactedDistanceDefaultInMetricUnits',
    MAP_SETTINGS_KEY: 'assetsImpactedDistanceOptionsInMetricUnits',
    UNIT_TRANSLATION_KEYS: {
      CAMEL: 'KILOMETERS-CAMEL',
      LOWER: 'KILOMETERS-LOWER',
      ABBREVIATED: 'KILOMETERS-ABBREVIATED',
      ABBREVIATED_LOWER: 'KILOMETERS-ABBREVIATED-LOWER'
    },
    MULTIPLIER: 1000
  }
};

@Injectable({ providedIn: 'root' })
export class ImpactedAssetsService {
  private store = inject(Store);
  private translate = inject(TranslateService);
  private api = inject(ImpactedAssetsApiService);

  getImpactedAssetsForDocument({
    dataset,
    document,
    useImperialUnits,
    distance = 0,
    unitDisplayOptions: { lowercase = false, abbreviated = false }
  }: ImpactedAssetsSearchParams): Observable<ImpactedAssetsSearchResults> {
    return forkJoin({
      mapState: this.store.select(COPSelectors.selectSelectedMapState).pipe(take(1)),
      distanceUnit: this.store.select(UserProfileSelectors.selectDistanceUnit).pipe(take(1)),
      organization: this.store.select(UserProfileSelectors.selectOrganization).pipe(take(1))
    }).pipe(
      switchMap(({ mapState: { layers, datasetsSet }, distanceUnit, organization }) => {
        const shouldUseImperialUnits = useImperialUnits !== undefined ? useImperialUnits : 'miles'.includes(distanceUnit.toLowerCase());
        const distanceOptionsConfigKey = shouldUseImperialUnits ? DISTANCE_UNITS.IMPERIAL : DISTANCE_UNITS.METRIC;

        const { UNIT_TRANSLATION_KEYS, DEFAULT_OPTIONS, DEFAULT_OPTION_VALUE_KEY, MAP_SETTINGS_KEY, MULTIPLIER } =
          DISTANCE_OPTIONS_CONFIG[distanceOptionsConfigKey];

        const unitTranslationKey =
          abbreviated && lowercase
            ? UNIT_TRANSLATION_KEYS.ABBREVIATED_LOWER
            : abbreviated
              ? UNIT_TRANSLATION_KEYS.ABBREVIATED
              : lowercase
                ? UNIT_TRANSLATION_KEYS.LOWER
                : UNIT_TRANSLATION_KEYS.CAMEL;

        const distanceUnitText = this.translate.instant(`UNITS.DISTANCE.${unitTranslationKey}`);

        const distanceOptions = [...((organization?.mapSettings[MAP_SETTINGS_KEY] as number[]) ?? DEFAULT_OPTIONS)]
          .sort((a, b) => a - b)
          .map(value => ({ value }));

        const selectedValue: number =
          distance > 0
            ? distance
            : (dataset[DEFAULT_OPTION_VALUE_KEY] ?? organization?.mapSettings[DEFAULT_OPTION_VALUE_KEY] ?? distanceOptions[1].value);

        const assetLayerMap = {};

        const layerStates = layers.reduce((acc, layer) => {
          if (layer.type === LayerTypes.Asset) {
            assetLayerMap[layer.uId] = layer;

            acc.push({
              layerUId: layer.uId,
              layerName: layer.name,
              datasetUId: layer.datasetUId,
              queryMatchRuleDto: {
                filterGroup: layer.rule?.root,
                fields: null,
                pagination: { take: 1000, skip: 0 }
              }
            });
          }

          return acc;
        }, [] as SelectionLayerFilter[]);

        const closestAssetsReq: ClosestAssetFilterDto = {
          layerStates,
          locationGeoJson: { filterGeo: document.geometry },
          distanceGeoJson: null,
          pagination: null,
          withinMeters: Math.round(selectedValue * MULTIPLIER)
        };

        return this.api.getClosestAssetCountsByLayer(closestAssetsReq).pipe(
          catchError(err => {
            console.error(err);
            return [];
          }),
          map((res: ClosestAssetCountResponse[]) => {
            const countsByLayer: ImpactedAssetLayerCount[] = res.map(layerCount => {
              const layer = assetLayerMap[layerCount.layerUId];
              const dataset = datasetsSet[layer?.datasetUId];

              return {
                ...layerCount,
                datasetType: dataset?.type,
                datasetSubtype: dataset?.subtype
              };
            });

            return {
              distance: selectedValue,
              distanceUnit: distanceUnitText,
              distanceOptions,
              countsByLayer
            };
          })
        );
      })
    );
  }
}
